import { useTranslation } from 'react-i18next'
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Facebook,
  Youtube,
  Instagram,
  BadgeCheck,
} from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

interface FooterLink {
  label: string
  routeKey: RouteKey
}

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/life-elettronica/', label: 'LinkedIn' },
  { icon: Facebook, href: 'https://www.facebook.com/lifeelettronica', label: 'Facebook' },
  { icon: Youtube, href: 'https://www.youtube.com/@lifeelettronica', label: 'YouTube' },
  { icon: Instagram, href: 'https://www.instagram.com/lifeelettronica/', label: 'Instagram' },
]

const departmentEmails = [
  { key: 'sales', email: 'sales@lifeelettronica.com' },
  { key: 'finance', email: 'finance@lifeelettronica.com' },
  { key: 'engineering', email: 'engineering@lifeelettronica.com' },
  { key: 'logistic', email: 'logistic@lifeelettronica.com' },
]

const certifications = [
  { badge: 'IATF 16949', descKey: 'footer.certAutomotive' },
  { badge: 'ISO/TS 22163', descKey: 'footer.certRailways' },
]

export function Footer() {
  const { t } = useTranslation('common')
  const currentYear = new Date().getFullYear()

  const navLinks: FooterLink[] = [
    { label: t('nav.home'), routeKey: 'home' },
    { label: t('nav.azienda'), routeKey: 'azienda' },
    { label: t('nav.persone'), routeKey: 'persone' },
    { label: t('nav.servizi'), routeKey: 'servizi' },
    { label: t('nav.contacts'), routeKey: 'contacts' },
  ]

  const legalLinks: FooterLink[] = [
    { label: t('footer.privacy'), routeKey: 'privacy' },
  ]

  return (
    <footer className="relative bg-ink text-white overflow-hidden">
      {/* Texture griglia PCB */}
      <div className="absolute inset-0 pcb-grid-dark opacity-60" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-14 lg:py-20">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {/* Colonna 1: Brand */}
            <div className="lg:col-span-1">
              <img
                src="/media/images/life-logo-footer-w.svg"
                alt={t('aria.logoAlt')}
                className="h-10 w-auto mb-5"
              />
              <p className="text-sm text-white/55 leading-relaxed mb-6 max-w-xs">
                {t('footer.tagline')}
              </p>

              <div className="flex gap-2.5">
                {socialLinks.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 hover:bg-primary-400 hover:border-primary-400 hover:text-ink transition-all duration-200"
                    aria-label={label}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </a>
                ))}
              </div>
            </div>

            {/* Colonna 2: Navigazione */}
            <div>
              <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-primary-400/80 mb-5">
                {t('footer.navigation')}
              </h3>
              <ul className="space-y-2.5">
                {navLinks.map((link) => (
                  <li key={link.routeKey}>
                    <LocalizedLink
                      to={link.routeKey}
                      className="text-sm text-white/65 hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Colonna 3: Contatti */}
            <div>
              <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-primary-400/80 mb-5">
                {t('footer.contacts')}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 text-white/65">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-400" />
                  <span>
                    Via Pietro Giardini, 1271/B<br />
                    41126 Modena MO, Italy
                  </span>
                </div>
                <a
                  href="tel:+39059512504"
                  className="flex items-center gap-3 text-white/65 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 flex-shrink-0 text-primary-400" />
                  +39 059.512504
                </a>
                {departmentEmails.map(({ key, email }) => (
                  <a
                    key={key}
                    href={`mailto:${email}`}
                    className="flex items-center gap-3 text-white/65 hover:text-white transition-colors"
                  >
                    <Mail className="h-4 w-4 flex-shrink-0 text-primary-400" />
                    <span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                        {t(`footer.dept.${key}`)}
                      </span>
                      <br />
                      {email}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Colonna 4: Certificazioni */}
            <div>
              <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-primary-400/80 mb-5">
                {t('footer.certifications')}
              </h3>
              <div className="space-y-3">
                {certifications.map(({ badge, descKey }) => (
                  <div
                    key={badge}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3"
                  >
                    <BadgeCheck className="h-5 w-5 flex-shrink-0 text-primary-400" />
                    <div>
                      <p className="font-mono text-xs font-semibold text-white/90">{badge}</p>
                      <p className="text-[11px] text-white/45">{t(descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Note legali */}
              <div className="mt-6">
                <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-primary-400/80 mb-3">
                  {t('footer.legalNotes')}
                </h3>
                <ul className="space-y-1.5">
                  {legalLinks.map((link) => (
                    <li key={link.routeKey}>
                      <LocalizedLink
                        to={link.routeKey}
                        className="text-xs text-white/50 hover:text-white/80 transition-colors"
                      >
                        {link.label}
                      </LocalizedLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barra inferiore */}
      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-3 py-6 font-mono text-[11px] tracking-wide text-white/40 sm:flex-row">
            <p>
              &copy; {currentYear} LIFE Elettronica Srl. {t('footer.allRightsReserved')}
            </p>
            <p>P.IVA 01948560367</p>
          </div>
        </div>
      </div>
    </footer>
  )
}
